import { stringify } from 'query-string';

export const applicationsDataProvider = context => {
    const basePath = `${context.apiUrl}/api-management/1.0/applications`;
    const basePathapi = `${context.apiUrl}/api-management/0.1/applications`;
    const scopeMapping = {
        FF: 'external:ffshipstat',
        IP: 'external:equipment',
        IM: 'external:intshipstat',
        DRAY: 'external:drayshipstat',
        TPL: 'external:tplcontract external:tplshipstat',
    };

    return {
        getManyReference: async ({
            pagination = { page: 1, perPage: 12 },
            filter: { id },
        }) => {
            const url = `${basePath}?${stringify({
                apiUuid: id,
                page: pagination.page - 1,
                size: pagination.perPage,
            })}`;

            const { json } = await context.fetchJson(url, {
                credentials: 'include',
            });

            return {
                data:
                    json.results.map(({ uuid, ...item }) => ({
                        id: uuid,
                        ...item,
                    })) || [],
                total: json.totalElements || 0,
            };
        },

        getList: async ({
            filter = {},
            pagination = { page: 1, perPage: 12 },
            sort = null,
        }) => {
            let payload = {
                ...filter,
                page: pagination.page - 1,
                size: pagination.perPage,
                ...(sort && { sort: `${sort.field},${sort.order}` }),
            };

            payload =
                localStorage.getItem('isSSO').toString() === 'true' &&
                localStorage.getItem('userType').toLowerCase() === 'internal'
                    ? { ...payload, name: 'internal' || 'testing' }
                    : payload;

            const url = `${basePath}?${stringify(payload)}`;

            const { json } = await context.fetchJson(url, {
                credentials: 'include',
            });

            return {
                data:
                    json.results.map(({ uuid, ...item }) => ({
                        id: uuid,
                        ...item,
                    })) || [],
                total: json.totalElements || 0,
            };
        },

        getOne: async ({ id }) => {
            const url = `${basePath}/${id}`;

            const {
                json: { Uuid, ...data },
            } = await context.fetchJson(url, { credentials: 'include' });

            const apiPlansResponse = await context.fetchJson(
                basePathapi + '/' + id + '/api-plans?',
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );
            const ApiIds = [];
            const ApiApiPlanIds = apiPlansResponse.json.map(apiplan => {
                ApiIds.push(apiplan.uuid);
                return {
                    ApiUuid: apiplan.uuid,
                    ApiPlanUuid: apiplan.ApiPlanUuid,
                };
            });

            const apiKeyResponse = await context.fetchJson(url + '/api-keys', {
                method: 'GET',
                credentials: 'include',
                Accept: 'application/json',
            });

            const customFieldsResponse = await context.fetchJson(
                url + '/custom-fields',
                {
                    method: 'GET',
                    credentials: 'include',
                    Accept: 'application/json',
                }
            );

            // Capitalized keys are not used in the new api format.
            // To avoid future breaking changes,
            // we transform the capitalized key.
            return {
                data: {
                    id: data.uuid,
                    name: data.name,
                    description: data.description,
                    status: data.status,
                    organizationUuid: data.organizationUuid,
                    organizationName: data.organizationName,
                    apiIds: { results: ApiIds },
                    apiApiPlanIds: { results: ApiApiPlanIds },
                    apiGroupIds: data.ApiGroupIds,
                    apiKey: apiKeyResponse?.json?.results[0]?.apiKey,
                    keySecret: apiKeyResponse?.json?.results[0]?.keySecret,
                    OauthScope: apiKeyResponse?.json?.results[0]?.oauthScope,
                    OauthType: apiKeyResponse?.json?.results[0]?.oauthType,
                    disabledByType: data.disabledByType,
                    customFieldValues: customFieldsResponse?.json || [],
                    ...data,
                },
            };
        },

        create: async ({ data }) => {
            // const url = `${context.baseUrl}/admin/Portal.svc/Applications`;
            const url = `${basePath}`;
            const urlapi = `${basePathapi}`;

            const createApplicationPayload = {
                name: data?.Name,
                description: data?.Description,
                organizationUuid: data?.OrganizationUuid,
            };

            const {
                json: { Uuid, ...responseData },
            } = await context.fetchJson(url, {
                credentials: 'include',
                method: 'POST',
                body: JSON.stringify(createApplicationPayload),
            });

            const customFieldsResponse = await context.fetchJson(
                context.apiUrl + '/api-management/1.0/custom-fields',
                {
                    method: 'GET',
                    credentials: 'include',
                    Accept: 'application/json',
                }
            );

            const customerResponse = await context.fetchJson(
                '/api/v1/customer/name/' + responseData.organizationName,
                {
                    credentials: 'include',
                }
            );

            const CreateKeyPayload = {
                name: 'Api key',
                applicationUuid: responseData.uuid,
                defaultKey: true,
                authMethod: 'SECRET',
                oauthCallbackUrl: data.OauthCallbackUrl,
                oauthScope: scopeMapping[customerResponse?.json?.customerType],
                oauthType: data.OauthType,
                keySecretHashed: data.ShouldHash,
            };

            const apiKeyResponse = await context.fetchJson(
                url + '/' + responseData.uuid + '/api-keys',
                {
                    method: 'POST',
                    body: JSON.stringify(CreateKeyPayload),
                    credentials: 'include',
                    Accept: 'application/json',
                }
            );

            const APIsPayload = data.ApiIds.results.map(id => {
                return { uuid: id };
            });

            const apiPlanResponse = await context.fetchJson(
                urlapi + '/' + responseData.uuid + '/api-plans?',
                {
                    method: 'PATCH',
                    body: JSON.stringify(APIsPayload),
                    credentials: 'include',
                    Accept: 'application/json',
                }
            );

            const customFieldPayload = [
                {
                    customFieldUuid:
                        customFieldsResponse?.json?.results[0]?.uuid,
                    value: customerResponse?.json?.id,
                },
            ];

            const customFieldResponse = await context.fetchJson(
                url + '/' + responseData.uuid + '/custom-fields',
                {
                    credentials: 'include',
                    method: 'PUT',
                    body: JSON.stringify(customFieldPayload),
                }
            );

            const applicationPublishResponse = await context.fetchJson(
                url + '/' + responseData.uuid + '/publish',
                {
                    method: 'PUT',
                    credentials: 'include',
                    Accept: 'application/json',
                }
            );

            return {
                data: {
                    id: responseData.uuid,
                    name: responseData.Name,
                    description: responseData.description,
                    status: responseData.Status,
                    organizationUuid: responseData.organizationUuid,
                    organizationName: responseData.organizationName,
                    apiIds: responseData.ApiIds,
                    apiApiPlanIds: responseData.ApiApiPlanIds,
                    apiGroupIds: responseData.ApiGroupIds,
                    apiKey: apiKeyResponse?.json?.apiKey,
                    keySecret: apiKeyResponse?.json?.keySecret,
                    disabledByType: responseData.DisabledByType,
                    ...responseData,
                },
            };
        },
        update: async ({ id, data }) => {
            const url = `${basePath}/${id}`;
            const urlapi = `${basePathapi}`;

            const updateApplicationPayload = {
                name: data?.Name,
                description: data?.Description,
                organizationUuid: data?.OrganizationUuid,
                organizationName: data?.OrganizationName,
                status: data?.Status,
                uuid: id,
            };
            const {
                json: { ...responseData },
            } = await context.fetchJson(url, {
                credentials: 'include',
                method: 'PUT',
                body: JSON.stringify(updateApplicationPayload),
            });

            const APIsPayload = data.ApiApiPlanIds.results.map(item => {
                return { uuid: item.ApiUuid };
            });

            const apiPlanResponse = await context.fetchJson(
                urlapi + '/' + id + '/api-plans?',
                {
                    method: 'PUT',
                    body: JSON.stringify(APIsPayload),
                    credentials: 'include',
                    Accept: 'application/json',
                }
            );

            if (data.Status !== 'DISABLED') {
                const apiKeyPayload = {
                    apiKey: data.ApiKey,
                    applicationUuid: id,
                    authMethod: 'SECRET',
                    defaultKey: true,
                    keySecretHashed: true,
                    name: 'API Key',
                    oauthCallbackUrl: data.OauthCallbackUrl,
                    oauthScope: data.OauthScope,
                    oauthType: data.OauthType,
                    status: 'ENABLED',
                };
                if (!data.KeySecret.includes('*')) {
                    apiKeyPayload['keySecret'] = data.KeySecret;
                }

                const apiKeyResponse = await context.fetchJson(
                    url + '/api-keys/' + data.ApiKey,
                    {
                        credentials: 'include',
                        method: 'PUT',
                        body: JSON.stringify(apiKeyPayload),
                    }
                );
            }

            const customerResponseUpdate = await context.fetchJson(
                '/api/v1/customer/name/' +
                    updateApplicationPayload.organizationName,
                {
                    credentials: 'include',
                }
            );

            const customFieldPayload = [
                {
                    customFieldUuid:
                        data.CustomFieldValues?.results[0]?.CustomFieldUuid,
                    value: customerResponseUpdate?.json?.id || '',
                },
            ];

            const customFieldResponse = await context.fetchJson(
                url + '/custom-fields',
                {
                    credentials: 'include',
                    method: 'PUT',
                    body: JSON.stringify(customFieldPayload),
                }
            );

            return {
                data: {
                    id: responseData.Uuid,
                    name: responseData.Name,
                    description: responseData.Description,
                    status: responseData.Status,
                    organizationUuid: responseData.OrganizationUuid,
                    organizationName: responseData.OrganizationName,
                    apiIds: responseData.ApiIds,
                    apiApiPlanIds: responseData.ApiApiPlanIds,
                    apiGroupIds: responseData.ApiGroupIds,
                    apiKey: responseData.ApiKey,
                    keySecret: responseData.KeySecret,
                    disabledByType: responseData.DisabledByType,
                    ...responseData,
                },
            };
        },

        getSecretHashMetadata: async () => {
            // const url = `${portal.hostname}/api/${portal.tenantPrefix}/Settings('APP_SECRET_HASHING_METADATA')`;
            const url = `${context.apiUrl}/tenant-admin/1.0/settings/APP_SECRET_HASHING_METADATA`;
            const {
                json: { uuid, ...data },
            } = await context.fetchJson(url, {
                credentials: 'include',
            });
            return {
                data: {
                    id: uuid,
                    name: data.name,
                    value: data.value,
                },
            };
        },

        getGenerateNewSharedSecret: async ({
            isPlainTextSelected,
            isHashedSecretSetting,
            id,
            record,
        }) => {
            const url =
                isHashedSecretSetting && isPlainTextSelected
                    ? `${context.apiUrl}/GenerateNewSharedSecret?ApplicationUuid='${id}'&ShouldHash='false'`
                    : `${context.apiUrl}/GenerateNewSharedSecret?ApplicationUuid='${id}'`;
            const { json } = await context.fetchJson(url, {
                credentials: 'include',
            });
            return {
                data: {
                    id,
                    keySecret: json.result,
                },
            };
        },

        checkApplicationUniqueness: async ({ applicationName }) => {
            const url = `${context.baseUrl}/admin/Portal.svc/ApplicationNameUnique()?Name='${applicationName}'`;
            const { json } = await context.fetchJson(url, {
                credentials: 'include',
            });
            return {
                data: {
                    isNameUnique: json.d.result,
                },
            };
        },

        delete: async ({ id }) => {
            const url = `${basePath}/${id}`;

            const {
                json: { ...data },
            } = await context.fetchJson(url, {
                credentials: 'include',
                method: 'DELETE',
            });

            return {
                data: { data },
            };
        },
    };
};
